// CreateApiKeyModal — opens stacked over ApiKeysModal, in two steps:
//   1. a required name + an optional free-text description;
//   2. after POST api-keys succeeds, the plain key — the only time the server
//      ever returns it — read-only with a Copy button and a "store it now"
//      warning. Closing from step 2 is the only way out, so the key can't be
//      dismissed by accident mid-create.
// The list refetches on success (cache invalidation).

import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Alert, Button, Field, Input, Modal, Textarea } from '@/components/common';
import { useCreateApiKeyMutation, type CreatedApiKey } from '@/api/endpoints/apiKeysApi';
import type { ApiErrorShape } from '@/api/types';
import { copyText } from '@/utils/clipboard';

interface CreateApiKeyModalProps {
  open: boolean;
  onClose: () => void;
}

const NAME_MAX = 100;
// A UI guard only — the contract sets no limit on the description.
const DESCRIPTION_MAX = 500;

const CreateApiKeyModal = ({ open, onClose }: CreateApiKeyModalProps) => {
  const { t } = useTranslation();
  const nameId = useId();
  const descriptionId = useId();
  const [createApiKey, { isLoading }] = useCreateApiKeyMutation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setSubmitted(false);
      setCreated(null);
    }
  }, [open]);

  const nameMissing = !name.trim();

  const submit = async () => {
    setSubmitted(true);
    if (nameMissing) return;
    const trimmed = description.trim();
    try {
      const result = await createApiKey({
        name: name.trim(),
        // Left blank → omitted; the list then reports null.
        ...(trimmed ? { description: trimmed } : {}),
      }).unwrap();
      setCreated(result);
    } catch (err) {
      // Duplicate names come back as a 400 with a readable message.
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  const copyKey = async () => {
    if (!created) return;
    try {
      await copyText(created.key);
      toast.success(t('api_keys_copied'));
    } catch {
      toast.error(t('error_generic'));
    }
  };

  if (created) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        className='create-api-key'
        icon='shield-check'
        tone='success'
        title={t('api_keys_created_title')}
        subtitle={t('api_keys_created_subtitle', { name: created.name })}
        footer={
          <Button variant='secondary' onClick={onClose}>
            {t('button_done')}
          </Button>
        }
      >
        <Alert variant='warning' icon='eye-off' title={t('api_keys_created_warning_title')}>
          {t('api_keys_created_warning')}
        </Alert>
        {/* The key is plain, wrapping text in a quiet panel — the whole thing
            is the secret, so it reads as one block with Copy beside it. */}
        <div className='create-api-key__key'>
          <code className='create-api-key__key-text'>{created.key}</code>
          <Button iconStart='copy' onClick={copyKey}>
            {t('api_keys_copy_key')}
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      className='create-api-key'
      icon='key-round'
      tone='success'
      title={t('api_keys_add_title')}
      subtitle={t('api_keys_add_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={isLoading}>
            {t('button_cancel')}
          </Button>
          <Button iconStart='key-round' onClick={submit} loading={isLoading}>
            {t('api_keys_add_submit')}
          </Button>
        </>
      }
    >
      <Field
        label={t('api_keys_name_label')}
        htmlFor={nameId}
        error={submitted && nameMissing ? t('api_keys_name_required') : undefined}
      >
        <Input
          id={nameId}
          autoFocus
          value={name}
          maxLength={NAME_MAX}
          placeholder={t('api_keys_name_ph')}
          error={submitted && nameMissing}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      <Field label={t('api_keys_description_label')} optional htmlFor={descriptionId}>
        <Textarea
          id={descriptionId}
          rows={4}
          value={description}
          maxLength={DESCRIPTION_MAX}
          placeholder={t('api_keys_description_ph')}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
    </Modal>
  );
};

export default CreateApiKeyModal;
